import React, { useState, useEffect } from 'react';
import { Calendar, DollarSign, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { addCampSession } from '@/services/campHierarchyService';
import { getCurrentSessionYear } from '@/utils/sessionExpiryHelpers';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Add Session Modal Component
 * Allows users to add a new session to a parent camp
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.parentCamp - Parent camp record from camp_audit_data or user_programs
 * @param {Function} props.onSessionAdded - Callback when session is successfully added
 */
const AddSessionModal = ({ isOpen, onClose, parentCamp, onSessionAdded }) => {
  const { toast } = useToast();
  const { user: authUser } = useAuth();
  const [formData, setFormData] = useState({
    session: '',
    session_year: getCurrentSessionYear(),
    session_price: '',
    start_date: '',
    end_date: '',
    age_range: '',
    grade_range: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [parentCampData, setParentCampData] = useState(parentCamp);
  const [isLoadingParentCamp, setIsLoadingParentCamp] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        session: '',
        session_year: getCurrentSessionYear(),
        session_price: '',
        start_date: '',
        end_date: '',
        age_range: '',
        grade_range: '',
      });
      setError('');
      setParentCampData(parentCamp);
    }
  }, [isOpen, parentCamp]);

  // Load parent camp data if not provided and we have an ID
  useEffect(() => {
    const loadParentCamp = async () => {
      if (parentCampData || !parentCamp?.id) return;

      setIsLoadingParentCamp(true);
      try {
        // Try to load from camp_audit_data first (by place_id or id)
        let { data, error: fetchError } = await supabase
          .from('camp_audit_data')
          .select('*')
          .or(`id.eq.${parentCamp.id},place_id.eq.${parentCamp.id}`)
          .maybeSingle();

        // If not found in camp_audit_data and parentCamp.id looks like a UUID, try user_programs
        if (!data && !fetchError && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(parentCamp.id)) {
          const { data: userProgramData, error: userProgramError } = await supabase
            .from('user_programs')
            .select('id, program_data')
            .eq('id', parentCamp.id)
            .maybeSingle();
          
          if (userProgramData && !userProgramError) {
            // Extract camp data from user_programs
            const pd = typeof userProgramData.program_data === 'string' 
              ? JSON.parse(userProgramData.program_data) 
              : userProgramData.program_data || {};
            
            // Create a camp-like object from user_programs data
            data = {
              id: pd.place_id || pd.id || parentCamp.id,
              place_id: pd.place_id || pd.id || parentCamp.id,
              name: pd.name,
              organization: pd.organization,
              program: pd.program,
              source: pd.source || 'user_entry',
              location: pd.location || pd.formatted_address || pd.general_location,
            };
          }
        }

        if (fetchError) throw fetchError;
        if (data) {
          setParentCampData(data);
        }
      } catch (err) {
        console.error('Error loading parent camp:', err);
        setError('Failed to load parent camp information.');
      } finally {
        setIsLoadingParentCamp(false);
      }
    };

    if (isOpen && parentCamp?.id && !parentCampData) {
      loadParentCamp();
    }
  }, [isOpen, parentCamp?.id, parentCampData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.session.trim()) {
      setError('Session name is required');
      return;
    }

    if (!formData.session_year) {
      setError('Session year is required');
      return;
    }

    try {
      setIsSaving(true);

      const effectiveParentCamp = parentCampData || parentCamp;
      
      // Validate parent camp has required data
      if (!effectiveParentCamp || !effectiveParentCamp.name) {
        throw new Error('Parent camp information is missing');
      }

      // If parentCamp doesn't have place_id, we need to find/create it in camp_audit_data first
      let parentCampWithPlaceId = effectiveParentCamp;
      if (!parentCampWithPlaceId.place_id) {
        const { findOrCreateCampAuditRecord } = await import('@/services/campHierarchyService');
        try {
          parentCampWithPlaceId = await findOrCreateCampAuditRecord({
            name: effectiveParentCamp.name,
            organization: effectiveParentCamp.organization || null,
            location: effectiveParentCamp.location || null,
            source: effectiveParentCamp.source || 'user_entry'
          });
          console.log('Created/found parent camp in camp_audit_data:', parentCampWithPlaceId);
        } catch (err) {
          console.error('Error finding/creating parent camp:', err);
          throw new Error(`Failed to find or create parent camp: ${err.message}`);
        }
      }

      if (!parentCampWithPlaceId.place_id) {
        throw new Error('Parent camp must have a place_id. Please ensure the camp exists in the database.');
      }

      const sessionData = {
        session: formData.session.trim(),
        session_year: parseInt(formData.session_year, 10),
        session_price: formData.session_price !== '' && formData.session_price != null ? parseFloat(formData.session_price) : null,
        start_date: (formData.start_date && String(formData.start_date).trim()) || null,
        end_date: (formData.end_date && String(formData.end_date).trim()) || null,
        age_range: (formData.age_range && String(formData.age_range).trim()) || null,
        grade_range: (formData.grade_range && String(formData.grade_range).trim()) || null,
      };

      console.log('Adding session with parentCamp:', parentCampWithPlaceId);
      console.log('Session data:', sessionData);

      if (!authUser?.id) {
        throw new Error('You must be logged in to add a session.');
      }

      const newSession = await addCampSession(parentCampWithPlaceId, sessionData, authUser);
      console.log('Session created successfully:', newSession);
      
      if (!newSession || !newSession.id) {
        throw new Error('addCampSession returned invalid session object');
      }

      toast({
        title: 'Session created',
        description: `Session "${formData.session}" has been added successfully.`,
      });

      // Reset form
      setFormData({
        session: '',
        session_year: getCurrentSessionYear(),
        session_price: '',
        start_date: '',
        end_date: '',
        age_range: '',
        grade_range: '',
      });

      // Call callback to refresh session list
      if (onSessionAdded) {
        await onSessionAdded(newSession, parentCampWithPlaceId.place_id);
      }

      // Close modal
      onClose();
    } catch (err) {
      console.error('Error adding session:', err);
      setError(err.message || 'Failed to add session. Please try again.');
      toast({
        title: 'Error',
        description: err.message || 'Failed to add session. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const effectiveParentCamp = parentCampData || parentCamp;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Calendar className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Add New Session</h2>
              <p className="text-sm text-gray-600 font-normal">Create a new session for the parent camp</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Loading state */}
        {isLoadingParentCamp && (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading parent camp information...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {!isLoadingParentCamp && !effectiveParentCamp && error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-900 mb-2">Parent Camp Not Found</h3>
            <p className="text-red-700 mb-4">{error}</p>
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        )}

        {/* Form */}
        {!isLoadingParentCamp && effectiveParentCamp && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Parent Camp Info */}
            {effectiveParentCamp && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Parent Camp</p>
                <p className="text-sm font-medium text-gray-900">{effectiveParentCamp.name}</p>
                {effectiveParentCamp.organization && (
                  <p className="text-xs text-gray-600 mt-1">{effectiveParentCamp.organization}</p>
                )}
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Session Name */}
            <div className="space-y-2">
              <Label htmlFor="session">Session Name *</Label>
              <Input
                id="session"
                value={formData.session}
                onChange={(e) => handleInputChange('session', e.target.value)}
                placeholder="e.g., Week 1, Session A, Cheerleading"
                required
                disabled={isSaving}
              />
            </div>

            {/* Session Year */}
            <div className="space-y-2">
              <Label htmlFor="session_year">Session Year *</Label>
              <Input
                id="session_year"
                type="number"
                value={formData.session_year}
                onChange={(e) => handleInputChange('session_year', e.target.value)}
                min="2020"
                max="2100"
                required
                disabled={isSaving}
              />
            </div>

            {/* Session Price */}
            <div className="space-y-2">
              <Label htmlFor="session_price">Price (Optional)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  id="session_price"
                  type="number"
                  step="0.01"
                  value={formData.session_price ?? ''}
                  onChange={(e) => handleInputChange('session_price', e.target.value)}
                  placeholder="0.00"
                  className="pl-9"
                  disabled={isSaving}
                />
              </div>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label htmlFor="start_date">Start Date (Optional)</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date || ''}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                disabled={isSaving}
              />
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label htmlFor="end_date">End Date (Optional)</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date || ''}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                disabled={isSaving}
              />
            </div>

            {/* Age Range (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="age_range">Age Range (Optional)</Label>
              <Input
                id="age_range"
                type="text"
                value={formData.age_range || ''}
                onChange={(e) => handleInputChange('age_range', e.target.value)}
                placeholder="e.g., 6-12 years"
                disabled={isSaving}
              />
            </div>

            {/* Grade Range (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="grade_range">Grade range (rising grade) (Optional)</Label>
              <Input
                id="grade_range"
                type="text"
                value={formData.grade_range || ''}
                onChange={(e) => handleInputChange('grade_range', e.target.value)}
                placeholder="e.g., Rising K–5th or Entering grades 2–6"
                disabled={isSaving}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Session'
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddSessionModal;
